import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    AuthorityTypeDto,
    DateFormatService,
    FlyFreelyError,
    FlyFreelyLoggingService,
    MissionRegisterStatusDetails,
    OrganisationAuthorityDto,
    OrganisationAuthorityService,
    PersonService,
    PersonnelRegisterEntryDetailsDto,
    PersonnelRegisterEntryDto,
    RegisterEntryVerificationActivityDto,
    RegisterEntryVerificationDetailsDto,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { AuthorityDialoguesService } from 'libs/authorities/src/lib/authority-dialogues.service';
import { matchConditionChecks } from 'libs/authorities/src/lib/registers/helpers';
import * as moment from 'moment-timezone';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject, forkJoin } from 'rxjs';
import { map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { PersonnelAddToRegisterDialogueComponent } from '../add-to-register-dialogue/personnel-add-to-register-dialogue.component';

interface Checks {
    heading: string;
    checked: boolean;
}

interface Conditions {
    status: PersonnelRegisterEntryDetailsDto.Status;
    checks: Checks[];
}

interface DisplayableRegister {
    registerName: string;
    missionTimeDetails: string;
    isMissionRegister: boolean;
    id: number;
    conditions: Conditions[];
    canView: boolean;
    authorityId: number;
    authorityRegisterId: number;
}

/**
 * Constructs a single string heading for a personnel register entry by combining organisation name,
 * authority details, register information, and (optional) mission status.
 *
 * @param register - The personnel register entry details containing authority and register information
 * @param isMissionRegister - Flag indicating whether this is a mission register entry
 * @returns A formatted string in the format: "Organisation's Authority Name [Identifier Label: Identifier] Register Name (Mission Register)"
 */
function constructRegisterNameString(
    register: PersonnelRegisterEntryDetailsDto,
    isMissionRegister: boolean
) {
    const organisationName = `${register.authority?.organisation.name}'s`;

    const authorityName =
        register.authority != null
            ? ` ${register.authority.authorityType.name}`
            : '';

    const authorityIdentifierLabel =
        register.authority?.authorityType.identifierLabel != null
            ? `${register.authority.authorityType.identifierLabel}: `
            : '';

    const authorityIdentifier =
        register.authority?.identifier != null
            ? ` [${authorityIdentifierLabel}${register.authority.identifier}]`
            : '';

    const registerName =
        register.authorityRegister != null
            ? ` ${register.authorityRegister.name}`
            : '';

    const missionRegisterLabel = isMissionRegister ? ` (Mission Register)` : '';

    return organisationName
        .concat(authorityName)
        .concat(authorityIdentifier)
        .concat(registerName)
        .concat(missionRegisterLabel);
}

@Component({
    selector: 'personnel-registers-status',
    templateUrl: './personnel-registers-status.component.html',
    styles: [
        `
            .status-column {
                width: 10%;
                min-width: 60px;
            }
        `
    ],
    styleUrls: ['./styles.scss']
})
export class PersonnelRegisterStatusComponent implements OnInit, OnDestroy {
    @Input() managingOrganisationId: number;
    @Input() personId: number;
    @Input() canEdit: boolean;
    @Input() canAdminOrganisation: boolean;
    @Input() missionDetails: MissionRegisterStatusDetails;
    @Input() hasCurrency: boolean;

    time: string;
    missionName: string;
    registers: DisplayableRegister[];
    allRegisters: PersonnelRegisterEntryDetailsDto[];
    allAuthorities: OrganisationAuthorityDto[];

    private ngUnsubscribe$ = new Subject<void>();
    private workTracker = new WorkTracker();
    working: boolean;

    constructor(
        private personService: PersonService,
        private authorityDialoguesService: AuthorityDialoguesService,
        private organisationAuthorityService: OrganisationAuthorityService,
        private logging: FlyFreelyLoggingService,
        private modalService: BsModalService,
        private dateFormatService: DateFormatService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        if (this.missionDetails == null) {
            this.time = moment().toISOString();
        } else {
            this.time = this.missionDetails.missionDate;
            this.missionName = this.missionDetails.missionName;
        }
        this.refreshData();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    refreshData() {
        this.registers = [];
        // First find the person's register authorities.
        this.personService
            .findPersonnelRegisterEntryByPersonId(
                this.personId,
                this.managingOrganisationId,
                this.time.toString()
            )
            .pipe(
                // Save the initial fetch to allRegisters without affecting the rest of the pipe
                tap(registers => (this.allRegisters = registers)),
                mergeMap(registers =>
                    forkJoin(
                        registers
                            .filter(r => r != null)
                            .map(register => {
                                // For each register, find the check values for that register.
                                // These will have the overall register status for that register that needs to be displayed
                                const registerConditionsHandler =
                                    this.organisationAuthorityService.personnelRegister(
                                        register.authority.id,
                                        register.id,
                                        this.managingOrganisationId
                                    );

                                return registerConditionsHandler
                                    .findCheckValues(register.id)
                                    .pipe(
                                        map(value => ({
                                            register: register,
                                            checkValue: value
                                        })),
                                        takeUntil(this.ngUnsubscribe$)
                                    );
                            })
                    )
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: result => this.mapRegisters(result),
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error updating person's registers: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    mapRegisters(
        checkResults: {
            register: PersonnelRegisterEntryDto;
            checkValue: RegisterEntryVerificationDetailsDto;
        }[]
    ) {
        if (this.allRegisters == null) {
            this.allRegisters = [];
            return;
        }

        const format = this.dateFormatService.getDateTimeFormat();
        this.registers = this.allRegisters
            .sort((a, b) =>
                this.missionDetails != null &&
                a.authority.id === this.missionDetails.delegatedAuthorityId
                    ? -1
                    : this.missionDetails != null &&
                      b.authority.id ===
                          this.missionDetails.delegatedAuthorityId
                    ? 1
                    : a.authority != null && b.authority != null
                    ? a.authority.authorityType.name.localeCompare(
                          b.authority.authorityType.name
                      )
                    : a.authorityRegister != null && b.authorityRegister != null
                    ? a.authorityRegister.name.localeCompare(
                          b.authorityRegister.name
                      )
                    : 0
            )
            .reduce((acc, r) => {
                const isMissionRegister =
                    this.missionDetails != null
                        ? r.authority.id ===
                          this.missionDetails.delegatedAuthorityId
                        : false;

                // If this is a mission register, it will show the register status for a specific time.
                // missionTimeDetails will display what that time is
                const missionTimeDetails =
                    this.missionName != null
                        ? isMissionRegister
                            ? `Relevant mission register status as at ${moment(
                                  this.time
                              ).format(format)} as required by ${
                                  this.missionName
                              }`
                            : `Showing register status as at ${moment(
                                  this.time
                              ).format(format)} as required by ${
                                  this.missionName
                              }`
                        : '';

                // Only show the status for checks that are enabled in the register so the display matches the authority register dialogue
                const checks: Checks[] = r.conditions
                    .filter(c => c.enabled)
                    .map(c => ({
                        heading: c.name,
                        checked:
                            r.checks.find(
                                check => check.authorityConditionId === c.id
                            ) != null
                                ? r.checks.find(check =>
                                      matchConditionChecks(c, check)
                                  )?.status ===
                                  RegisterEntryVerificationActivityDto.Status
                                      .PASSED
                                : false
                    }));

                if (acc.find(register => register.id === r.id) == null) {
                    return acc.concat({
                        registerName: constructRegisterNameString(
                            r,
                            isMissionRegister
                        ),
                        missionTimeDetails: missionTimeDetails,
                        id: r.id,
                        authorityRegisterId: r.authorityRegisterId,
                        isMissionRegister: isMissionRegister,
                        conditions: [
                            {
                                // The status should be the overall register status, which is in the check results
                                status: checkResults.find(
                                    result => result.register.id === r.id
                                ).checkValue.status,
                                checks: checks
                            }
                        ],
                        canView:
                            r.authority.organisation.id ===
                            this.managingOrganisationId,
                        authorityId: r.authority?.id
                    });
                } else {
                    acc.find(register => register.id === r.id).conditions.push({
                        // The status should be the overall register status, which is in the check results
                        status: checkResults.find(
                            result => result.register.id === r.id
                        ).checkValue.status,
                        checks: checks
                    });
                }
            }, []);
    }

    showRegister(register: DisplayableRegister) {
        const reg = this.allRegisters.find(r => register.id === r.id);
        this.organisationAuthorityService
            .findAuthority(reg.authority.id, this.managingOrganisationId)
            .subscribe(
                authority => {
                    const dialogue =
                        this.authorityDialoguesService.showPersonnelRegisterDialogue(
                            reg.authority.organisation.id,
                            <AuthorityTypeDto>reg.authority.authorityType,
                            authority,
                            reg.authorityRegister,
                            this.managingOrganisationId
                        );

                    dialogue.onHidden
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe(() => this.refreshData());
                },
                (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error viewing register: ${error.message}`
                    );
                }
            )
            .add(this.workTracker.createTracker());
    }

    showAddToRegisterDialogue() {
        const dialogue = this.modalService.show(
            PersonnelAddToRegisterDialogueComponent,
            {
                class: 'modal-md',
                initialState: {
                    personId: this.personId,
                    managingOrganisationId: this.managingOrganisationId
                }
            }
        );

        dialogue.onHidden
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshData());
    }
}
